// CSRS 4200 checklist builder and status counter.
//
// The Review tab's checklist is built from raw engagement inputs (balance sheet,
// income statement, transactions, AJEs, compilation data, notes selected,
// cross-check results). This file is the pure data layer for that — it returns
// the checklist array and a counts object, and the React class just renders.

import { NOTE_IDS } from "../../../shared/helpers/constants";
import BookkeepingService from "../../../../../../../services/BookkeepingService";
import AFSService from "../../../../../../../services/AFSService";

// ─── Going-concern indicators (ASPE 1400) ─────────────────────────────────────

function computeGoingConcern(balanceSheet, incomeStatement) {
	let currentAssetTotal = 0;
	let currentLiabTotal = 0;
	for (let section of (balanceSheet?.sections || [])) {
		if (section.category === "current-assets") currentAssetTotal += (section.totalCurrent || 0);
		if (section.category === "current-liabilities") currentLiabTotal += (section.totalCurrent || 0);
	}
	let workingCapital = currentAssetTotal - currentLiabTotal;
	let totalEquity = balanceSheet?.totalEquity?.current || 0;
	let netIncome = incomeStatement?.netIncome?.current || 0;
	let priorNetIncome = incomeStatement?.netIncome?.prior || 0;

	let hasNegativeWorkingCapital = workingCapital < 0;
	let hasNegativeEquity = totalEquity < 0;
	let hasSustainedLosses = netIncome < 0 && priorNetIncome < 0;
	let hasIndicators = hasNegativeWorkingCapital || hasNegativeEquity || hasSustainedLosses;

	let details = [];
	if (hasNegativeWorkingCapital) details.push("Negative working capital ($" + Math.round(workingCapital).toLocaleString("en-CA") + ")");
	if (hasNegativeEquity) details.push("Negative equity ($" + Math.round(totalEquity).toLocaleString("en-CA") + ")");
	if (hasSustainedLosses) details.push("Sustained losses (current and prior year)");

	return { hasIndicators: hasIndicators, details: details };
}

// ─── Classification progress over the transaction list ───────────────────────

function computeClassificationProgress(transactions) {
	let total = (transactions || []).length;
	let classified = 0;
	for (let t of (transactions || [])) {
		let cls = t.classification || "";
		if (cls && cls !== "Unclassified" && cls !== "uncategorized") classified++;
	}
	let percentage = total > 0 ? Math.round((classified / total) * 100) : 100;
	return { total: total, classified: classified, unclassified: total - classified, percentage: percentage, allClassified: percentage === 100 };
}

// ─── Cross-check section grouping ────────────────────────────────────────────

function buildCrossCheckSections(crossCheckResults) {
	let allChecks = crossCheckResults || [];
	if (allChecks.length === 0) {
		return [{
			section: "Section Cross-Checks",
			ref: "",
			items: [{
				id: "xcheck_none",
				auto: true,
				label: "No cross-check results available",
				detail: "Run the compilation to generate cross-check results",
				status: "warn",
			}],
		}];
	}

	let bySection = {};
	for (let check of allChecks) {
		let name = check.section || "Other";
		if (!bySection[name]) bySection[name] = [];
		bySection[name].push(check);
	}

	let groups = [];
	for (let sectionName in bySection) {
		let sectionChecks = bySection[sectionName];
		let items = sectionChecks.map(crossCheckToItem);
		if (items.length > 0) {
			groups.push({ section: sectionName, ref: sectionChecks.length + " checks", items: items });
		}
	}
	return groups;
}

function crossCheckToItem(check) {
	let status;
	let detail;
	if (!check.applicable) {
		status = "pass";
		detail = "N/A — " + (check.skipped_because || "not applicable for this entity type");
	} else if (check.status === "blocked") {
		status = "blocked";
		detail = check.detail || "Blocked — missing required data";
	} else if (check.passed) {
		status = "pass";
		detail = check.passed_reason || "Passed";
	} else if (check.tier === 1) {
		status = "fail";
		detail = check.detail || "Structural contradiction";
	} else if (check.tier === 2) {
		status = "warn";
		detail = check.detail || "Anomaly detected";
	} else {
		status = "info";
		detail = check.detail || "Advisory";
	}
	return {
		id: "xcheck_" + check.id,
		auto: true,
		label: check.applicable && check.passed && check.passed_label ? check.passed_label : check.label,
		detail: detail,
		status: status,
		crossCheck: true,
		tier: check.tier,
		checkId: check.id,
		findings: check.findings || null,
		analytic_type: check.analytic_type || null,
	};
}

// ─── Notice date gap (report-dated-after-year-end check) ─────────────────────

function computeReportDateGap(noticeDate, yearEnd) {
	if (!noticeDate || !yearEnd) {
		return { months: 0, tooSoon: false, warning: false };
	}
	let noticeMs = new Date(noticeDate).getTime();
	let yearEndMs = new Date(yearEnd).getTime();
	let months = Math.round((noticeMs - yearEndMs) / (1000 * 60 * 60 * 24 * 30));
	return { months: months, tooSoon: months <= 0, warning: months > 6 };
}

// ─── The main builder ────────────────────────────────────────────────────────

export function buildCsrsChecklist({
	balanceSheet, incomeStatement, transactions, adjustingEntries, ppeNote,
	compilationData, compilationReportSignature, compilationReport, addedNotes, entityName, yearEnd, crossCheckResults,
	importedTrialBalance,
}) {
	let signature = compilationReportSignature || {};
	let printedParagraphs = (compilationReport && compilationReport.paragraphs) || [];
	let printedReport = printedParagraphs.join(" ").toLowerCase();
	let classification = computeClassificationProgress(transactions);
	let goingConcern = computeGoingConcern(balanceSheet, incomeStatement);

	let hasAccepted = !!compilationData;
	let hasFramework = compilationData && compilationData.basisOfAccounting;
	let hasBusinessActivity = compilationData && compilationData.businessActivity;
	let hasNoticeDate = !!signature.reportDate;
	let hasCompilationData = printedParagraphs.length > 0;
	let hasEntityInfo = entityName && yearEnd;
	let hasTrialBalance = balanceSheet && balanceSheet.sections && balanceSheet.sections.length > 0;
	let recordsDetail = "Trial balance built from the books — " + (balanceSheet.sections || []).length + " balance sheet sections";
	if (!hasTrialBalance) recordsDetail = "No trial balance yet — the books have not produced statements";
	else if (importedTrialBalance) recordsDetail = "The books are " + BookkeepingService.describeTheBooksSource({ importedTrialBalance }) + ", kept on the file";
	let hasAJEs = (adjustingEntries || []).length > 0;
	let hasPPE = ppeNote && ppeNote.classes && ppeNote.classes.length > 0;
	let noticeHasRestriction = printedReport.includes("readers are cautioned");

	let compilationNotes = (compilationData && Array.isArray(compilationData.notes)) ? compilationData.notes : [];
	let allNotes = [...compilationNotes, ...(addedNotes || [])];
	let hasNoteBasis = allNotes.includes(NOTE_IDS.BASIS);
	let hasNotePolicies = allNotes.includes(NOTE_IDS.POLICIES);
	let hasNotePPE = allNotes.includes(NOTE_IDS.PPE);
	let hasNoteBusiness = allNotes.includes(NOTE_IDS.BUSINESS);
	let hasNoteGoingConcern = allNotes.includes(NOTE_IDS.GOING_CONCERN);

	let reportDate = computeReportDateGap(signature.reportDate || null, yearEnd);
	let totalTransactions = classification.total;

	let groups = [
		{
			section: "Engagement Acceptance",
			ref: "CSRS 4200.07 – .10",
			items: [
				{ id: "accept_terms", auto: true, label: "Engagement terms agreed with management", detail: hasAccepted ? "Compilation Agent acceptance completed — management understands this is a compilation" : "Run the Compilation Agent to confirm engagement terms", status: hasAccepted ? "pass" : "fail" },
				{ id: "accept_responsibility", auto: true, label: "Management accepts responsibility for the financial information", detail: hasAccepted ? "Management responsibility acknowledged in Compilation Agent" : "Not yet confirmed — complete Compilation Agent step 1", status: hasAccepted ? "pass" : "fail" },
				{ id: "accept_framework", auto: true, label: "Financial reporting framework is acceptable", detail: hasFramework ? "Framework selected: " + (hasFramework === "aspe" ? "ASPE" : hasFramework === "tax" ? "Tax basis" : compilationData.basisOfAccounting) : "No framework selected", status: hasFramework ? "pass" : "fail" },
				{ id: "accept_ethics", auto: true, label: "Ethical requirements satisfied", detail: hasAccepted ? "Professional competence and objectivity confirmed" : "Not yet confirmed", status: hasAccepted ? "pass" : "fail" },
			],
		},
		{
			section: "Planning & Understanding",
			ref: "CSRS 4200.11 – .13",
			items: [
				{ id: "plan_industry", auto: true, label: "Understanding of the entity and its industry obtained", detail: hasBusinessActivity ? "Business activity recorded: " + compilationData.businessActivity : "Business activity not documented — set in Compilation Agent step 2", status: hasBusinessActivity ? "pass" : "warn" },
				{ id: "plan_accounting", auto: true, label: "Accounting policies are appropriate for the framework", detail: (hasNoteBasis && hasNotePolicies) ? "Basis of accounting and significant policies notes selected" : "Select basis of accounting and policies in the Notes checklist", status: (hasNoteBasis && hasNotePolicies) ? "pass" : "warn" },
				{ id: "plan_records", auto: true, label: "Accounting records are adequate for compilation", detail: recordsDetail, status: hasTrialBalance ? "pass" : "fail" },
			],
		},
		{
			section: "Performing the Engagement",
			ref: "CSRS 4200.14 – .16",
			items: [
				importedTrialBalance
					? { id: "perform_classification", auto: true, label: "All transactions classified", detail: "N/A — the books are a trial balance, so there are no transactions to classify; each balance was linked to a statement line when it was read", status: "pass" }
					: { id: "perform_classification", auto: true, label: "All transactions classified", detail: classification.allClassified ? classification.classified + "/" + classification.total + " classified (100%)" : classification.unclassified + " unclassified (" + classification.percentage + "% complete)", status: classification.allClassified ? "pass" : classification.percentage >= 90 ? "warn" : "fail" },
				// BS balance and RE continuity are now handled by the cross-check engine (BS_01, RE_01)
				{ id: "perform_adjustments", auto: true, label: "Adjusting entries reviewed", detail: hasAJEs ? (adjustingEntries || []).length + " adjusting entries recorded" : "No adjusting entries — confirm year-end adjustments are not required", status: hasAJEs ? "pass" : "warn" },
				{ id: "perform_going_concern", auto: true, label: "Going concern assessed (ASPE 1400)", detail: goingConcern.hasIndicators ? "Indicators detected: " + goingConcern.details.join("; ") + (hasNoteGoingConcern ? " — going concern note included" : " — consider adding a going concern note") : "No going concern indicators detected", status: goingConcern.hasIndicators ? (hasNoteGoingConcern ? "warn" : "fail") : "pass" },
				{ id: "perform_reviewed_bs", label: "Balance sheet reviewed for accuracy", detail: "All account balances reviewed and confirmed reasonable" },
				{ id: "perform_reviewed_is", label: "Income statement reviewed for accuracy", detail: "Revenue, expenses, and net income reviewed for completeness" },
				{ id: "perform_plausibility", label: "Financial information is plausible on its face", detail: "Nothing obviously incorrect, incomplete, or misleading identified" },
				{ id: "perform_subsequent", label: "Subsequent events considered (CSRS 4200.22)", detail: "No material events after year-end that would require adjustment or disclosure have come to the practitioner's attention" },
			],
		},
	];

	// Cross-check sections come from the compilation engine (one group per
	// balance-sheet/income-statement section, e.g. Capital Assets, Equity, AR).
	for (let group of buildCrossCheckSections(crossCheckResults)) {
		groups.push(group);
	}

	groups.push({
		section: "Notes & Disclosures",
		ref: "CSRS 4200.14(b)",
		items: [
			{ id: "notes_basis", auto: true, label: "Note 1 — Basis of accounting described", detail: hasNoteBasis ? "Basis of accounting note included in financial information" : "Required — add the basis of accounting note", status: hasNoteBasis ? "pass" : "fail", actionable: !hasNoteBasis, actionLabel: "Add Basis of Accounting Note", actionNoteId: NOTE_IDS.BASIS },
			{ id: "notes_policies", auto: true, label: "Significant accounting policies disclosed", detail: hasNotePolicies ? "Accounting policies note included" : "Recommended — add accounting policies note", status: hasNotePolicies ? "pass" : "warn", actionable: !hasNotePolicies, actionLabel: "Add Accounting Policies Note", actionNoteId: NOTE_IDS.POLICIES },
			{ id: "notes_ppe", auto: true, label: "PP&E continuity schedule included", detail: !hasPPE ? "No PP&E on balance sheet — not applicable" : (hasNotePPE ? "PP&E continuity note included" : "PP&E exists — add the continuity schedule note"), status: !hasPPE ? "pass" : hasNotePPE ? "pass" : "warn", actionable: hasPPE && !hasNotePPE, actionLabel: "Add PP&E Continuity Note", actionNoteId: NOTE_IDS.PPE },
			{ id: "notes_business", auto: true, label: "Description of business included", detail: hasNoteBusiness ? "Description of business note included" : "Standard practice for NTR — add a business description note", status: hasNoteBusiness ? "pass" : "warn", actionable: !hasNoteBusiness, actionLabel: "Add Business Description Note", actionNoteId: NOTE_IDS.BUSINESS },
			{ id: "notes_related", label: "Related party transactions disclosed (if applicable)", detail: "ASPE 3840 — review for transactions with shareholders, directors, or related entities" },
			{ id: "notes_complete", label: "All required notes reviewed and complete", detail: "Notes are appropriate for the financial reporting framework" },
		],
	}, {
		section: "Compilation Report",
		ref: "CSRS 4200.17 – .19",
		items: [
			{ id: "report_prepared", auto: true, label: "Compilation engagement report prepared", detail: hasCompilationData ? "CSRS 4200 compilation notice generated" : "Not yet prepared — run the Compilation Agent", status: hasCompilationData ? "pass" : "fail" },
			{ id: "report_entity", auto: true, label: "Report identifies the entity and period", detail: hasEntityInfo ? "Entity: " + entityName + ", Year ended: " + yearEnd : "Entity or period not set", status: hasEntityInfo ? "pass" : "fail" },
			{ id: "report_framework", auto: true, label: "Report identifies the financial reporting framework", detail: hasFramework ? "Framework: " + (compilationData.basisOfAccounting === "aspe" ? "ASPE" : compilationData.basisOfAccounting === "tax" ? "Tax basis" : compilationData.basisOfAccounting) : "Framework not specified", status: hasFramework ? "pass" : "fail" },
			{ id: "report_restriction", auto: true, label: "Distribution restriction included", detail: noticeHasRestriction ? "\"Readers are cautioned\" paragraph present in compilation notice" : "Restriction paragraph not found in notice text", status: noticeHasRestriction ? "pass" : "warn" },
			{ id: "report_signed", auto: true, label: "Report dated for signature", detail: hasNoticeDate ? "Report dated: " + signature.reportDate : "Report date not set — choose the date the compilation was completed. It is printed on the report, and the file cannot be signed off without it.", status: hasNoticeDate ? "pass" : "fail", setsReportDate: !hasNoticeDate, suggestedReportDate: AFSService.suggestCompilationReportDate({ yearEnd }) },
			{ id: "report_date_gap", auto: true, label: "Report date is within reasonable timeframe", detail: hasNoticeDate ? (reportDate.tooSoon ? "Report date is on or before year-end — compilation cannot be completed before the period ends" : reportDate.warning ? "Report date is " + reportDate.months + " months after year-end — confirm this is appropriate" : "Report date is " + reportDate.months + " month" + (reportDate.months === 1 ? "" : "s") + " after year-end") : "Report date not set — setting it above settles this check too", status: !hasNoticeDate ? "fail" : (reportDate.tooSoon || reportDate.warning) ? "warn" : "pass", setsReportDate: hasNoticeDate && (reportDate.tooSoon || reportDate.warning), suggestedReportDate: hasNoticeDate ? signature.reportDate : AFSService.suggestCompilationReportDate({ yearEnd }) },
		],
	}, {
		section: "File Documentation",
		ref: "CSRS 4200.20 – .21",
		items: [
			{ id: "doc_engagement_letter", label: "Engagement letter on file", detail: "Signed engagement letter or written agreement of terms obtained" },
			{ id: "doc_management_rep", auto: true, label: "Management representation obtained", detail: hasAccepted ? "Acknowledgement recorded through Compilation Agent" : "Not yet confirmed", status: hasAccepted ? "pass" : "fail" },
			importedTrialBalance
				? { id: "doc_working_papers", auto: true, label: "Working papers documented", detail: "Source kept on file: " + BookkeepingService.describeTheBooksSource({ importedTrialBalance }) + ", " + (adjustingEntries || []).length + " AJEs" + (!hasAJEs ? " — confirm whether year-end adjustments are required" : ""), status: hasAJEs ? "pass" : "warn" }
				: { id: "doc_working_papers", auto: true, label: "Working papers documented", detail: (hasTrialBalance && totalTransactions > 0) ? totalTransactions + " transactions and " + (adjustingEntries || []).length + " AJEs on file" + (!hasAJEs ? " — confirm whether year-end adjustments are required" : "") : "Incomplete — ensure all source documents are retained", status: (!hasTrialBalance || totalTransactions === 0) ? "warn" : !hasAJEs ? "warn" : "pass" },
			{ id: "doc_final_review", label: "Final review completed by engagement partner", detail: "Partner has reviewed the compiled financial information before release" },
		],
	});

	return groups;
}

// ─── Status counting ─────────────────────────────────────────────────────────
//
// Walks the built checklist and returns the bucketed counts the summary bar
// shows. Also folds in manual sign-off completions (kept in state) and the AI
// recommendation acknowledgements, and decides whether the engagement is
// ready for export (no failed checks, every manual item signed off, every
// tier-2 cross-check acknowledged).

export function countChecklistStatuses({
	csrsChecklist, manualChecks, aiReview, recommendationAcknowledged,
	crossCheckResults, crossCheckAcknowledged,
}) {
	let manualCheckItems = [];
	for (let group of csrsChecklist) {
		for (let item of group.items) {
			if (!item.auto) manualCheckItems.push(item);
		}
	}

	let passCount = 0;
	let failCount = 0;
	let warnCount = 0;
	let infoCount = 0;
	let naCount = 0;
	let blockedCount = 0;
	let autoCheckCount = 0;

	for (let group of csrsChecklist) {
		for (let item of group.items) {
			if (!item.auto) continue;
			autoCheckCount++;
			let isNA = item.detail && item.detail.startsWith("N/A");
			if (isNA) { naCount++; passCount++; }
			else if (item.status === "blocked") { blockedCount++; failCount++; }
			else if (item.status === "pass") passCount++;
			else if (item.status === "fail") failCount++;
			else if (item.status === "info") infoCount++;
			else warnCount++;
		}
	}

	let manualCompleteCount = 0;
	for (let item of manualCheckItems) {
		if (manualChecks[item.id]) {
			manualCompleteCount++;
			passCount++;
		}
	}

	let totalRecommendations = aiReview && aiReview.recommendations ? aiReview.recommendations.length : 0;

	let acknowledgedCount = 0;
	for (let recKey of Object.keys(recommendationAcknowledged)) {
		if (recommendationAcknowledged[recKey]) acknowledgedCount++;
	}

	let totalChecks = autoCheckCount + manualCheckItems.length + totalRecommendations;
	let completedChecks = passCount + manualCompleteCount + acknowledgedCount;

	// Tier 2 cross-checks require acknowledgement before sign-off.
	let unacknowledgedTier2 = (crossCheckResults || []).filter(function (c) {
		return c.applicable && !c.passed && c.tier === 2 && !crossCheckAcknowledged[c.id];
	}).length;

	let isReadyForExport = failCount === 0 && manualCompleteCount === manualCheckItems.length && unacknowledgedTier2 === 0;

	return {
		manualCheckItems: manualCheckItems,
		passCount: passCount,
		failCount: failCount,
		warnCount: warnCount,
		infoCount: infoCount,
		naCount: naCount,
		blockedCount: blockedCount,
		manualCompleteCount: manualCompleteCount,
		totalRecommendations: totalRecommendations,
		acknowledgedCount: acknowledgedCount,
		totalChecks: totalChecks,
		completedChecks: completedChecks,
		unacknowledgedTier2: unacknowledgedTier2,
		isReadyForExport: isReadyForExport,
	};
}
